import { IFolderGridFile, OwnerHeaderComponent } from '@unified-ui/shared-components';
import { customAgGridTheme } from '@unified-ui/utilities';
import { GetRowIdParams, GridOptions, IRowNode, ITooltipParams, RowSelectionOptions } from 'ag-grid-community';
import { DateTime } from 'luxon';

/**
 * Default filter parameters for AG Grid columns
 */
export const DEFAULT_FILTER_PARAMS = {
    maxNumConditions: 1,
    convertValuesToStrings: true,
    suppressFilterButton: true
};

/**
 * Default column definition for AG Grid
 */
export const DEFAULT_COL_DEF = {
    sortable: true,
    resizable: true,
    editable: false,
    suppressSizeToFit: false,
    suppressAutoSize: false,
    floatingFilter: true,
    suppressHeaderMenuButton: true,
    filter: true,
    tooltipValueGetter: (params: ITooltipParams<IFolderGridFile>) => (params.data?.type === 'folder' ? 'Double click row to expand or collapse folder' : 'Double click row to view report'),
    suppressColumnsToolPanel: true,
    filterParams: DEFAULT_FILTER_PARAMS
};

/**
 * Status bar configuration for AG Grid
 */
export const STATUS_BAR_CONFIG = {
    statusPanels: [{ statusPanel: 'agTotalRowCountComponent' }, { statusPanel: 'agFilteredRowCountComponent' }]
};

/**
 * Creates default row selection options for AG Grid
 * @param isRowSelectableFn Function to determine if a row is selectable
 * @returns Row selection options configuration
 */
export const createDefaultRowSelectionOptions = (isRowSelectableFn: (data: any) => boolean): RowSelectionOptions => ({
    mode: 'multiRow',
    checkboxes: true,
    enableClickSelection: false,
    enableSelectionWithoutKeys: true,
    isRowSelectable: (rowNode: IRowNode) => isRowSelectableFn(rowNode.data),
    hideDisabledCheckboxes: true
});

/**
 * Creates base grid options with row selection configuration
 * @param isRowSelectableFn Optional function to determine if a row is selectable
 * @returns Grid options with row selection configured
 */
export const createBaseGridOptions = (isRowSelectableFn?: (data: any) => boolean): GridOptions => {
    const options: GridOptions = {
        treeData: true,
        components: {
            ownerHeaderComponent: OwnerHeaderComponent
        },
        treeDataParentIdField: 'parentId',
        groupDisplayType: 'custom',
        getRowId: (params: GetRowIdParams) => params.data.id,
        statusBar: STATUS_BAR_CONFIG,
        defaultColDef: DEFAULT_COL_DEF,
        columnDefs: [
            {
                field: 'id',
                colId: 'id',
                headerName: 'Id',
                hide: true,
                floatingFilter: false,
                filter: false,
                suppressColumnsToolPanel: true
            }
        ],
        tooltipShowDelay: 3,
        rowGroupPanelShow: 'never',
        headerHeight: 35,
        rowHeight: 24,
        animateRows: true,
        multiSortKey: 'ctrl',
        copyHeadersToClipboard: true,
        suppressLastEmptyLineOnPaste: true,
        theme: customAgGridTheme,
        suppressCellFocus: true
    };

    // Add row selection if a selection function is provided
    if (isRowSelectableFn) {
        options.rowSelection = createDefaultRowSelectionOptions(isRowSelectableFn);
    }

    return options;
};

/**
 * Base grid options for report list (for backward compatibility)
 */
export const baseGridOptions: GridOptions = createBaseGridOptions();



/**
 * Creates a date comparator function for AG Grid date filtering
 * @returns Date comparator function
 */
export const createDateComparator = () => {
    return (filterLocalDateAtMidnight: Date, cellValue: string | number | Date): number => {
        if (!cellValue) return 0;

        // Convert filter date to Luxon DateTime at the start of the day
        const filterDate = DateTime.fromJSDate(filterLocalDateAtMidnight).startOf('day');

        // Convert cellValue to Luxon DateTime, depending on its type
        let cellDate: DateTime;
        if (typeof cellValue === 'string') {
            cellDate = DateTime.fromISO(cellValue).startOf('day'); // Assumes ISO string format
        } else if (typeof cellValue === 'number') {
            cellDate = DateTime.fromSeconds(cellValue).startOf('day'); // Assumes Unix timestamp in seconds
        } else {
            cellDate = DateTime.fromJSDate(cellValue).startOf('day'); // Assumes Date object
        }

        if (!cellDate.isValid) {
            return 0;
        }

        // Compare the dates without the time component
        if (cellDate < filterDate) {
            return -1;
        } else if (cellDate > filterDate) {
            return 1;
        } else {
            return 0;
        }
    };
};

/**
 * Creates a date formatter function for AG Grid date columns
 * @returns Date formatter function
 */
export const createDateFormatter = () => {
    return (params: { value: string | number | Date | undefined }) => {
        let date: DateTime | undefined;

        if (typeof params.value === 'number') {
            // Treat the value as a Unix timestamp (in seconds)
            date = DateTime.fromSeconds(params.value);
        } else {
            date = params.value instanceof Date ? DateTime.fromJSDate(params.value) : undefined;
        }

        if (!date || !date.isValid) {
            return '';
        }

        return date.toFormat('yyyy-MM-dd');
    };
};

// For backward compatibility
export const gridOptions = baseGridOptions;
